/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, createContext, useState, useRef } from 'react';
import { Box, LinearProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ITeam, IUser, ITenantAccess } from '../interfaces/IUser';
import KeycloakService from '../services/KeycloakService';
import DatahubAPI from '../api/DatahubAPI';
import UsersAPI from 'api/UsersAPI';
import GroupAPI from 'api/GroupAPI';
import CreditsAPI from 'api/CreditsAPI';
import PaymentsAPI from 'api/PaymentsAPI';

import TenantAPI from 'api/TenantAPI';
import ScimAPI from 'api/ScimAPI';
import { LicensesAPI } from 'api/LicensesAPI';
import { loadDraft, registerRecoverable } from 'services/sessionRecovery';
// eslint-disable-next-line
const entMonthlyPrice = process.env.REACT_APP_BITE_ENT_MONTHLY!;
// eslint-disable-next-line
const BITE_DOMAINS: string = process.env.REACT_APP_BITE_DOMAINS!;
// Shared across all three "BITE Workflow" pages (Import, Export, Beneficial
// Ownership) -- see ticketDraft below.
const TICKET_DRAFT_RECOVERY_KEY = 'workflow-ticket-draft';
export interface ICase {
  _id: string;
  date: Date;
  ticketName: string;
  autoId: string;
  entity: ICreateEntityInfo;
  commodity: ICreateCommodityInfo;
  documents: ICreateCaseDocument[];
  comments: ICreateComments[];
  owner: IUser;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  updatedBy: string;
  assignee: string;
  groupId: string;
  groupName: string;
  userId: string;
  docType: string;
  results: ICreateResults[];
  searchedProps: string[];
  workflowEntry: IWorkflowEntry;
  workflowData: any[];
  sharedWith: string[];
  status: string;
  type: string;
  entityFilter: ISearchFilter;
  commodityFilter: ISearchFilter;
  frequency: string;
  lastUpdate: string;
  curTotal: number;
  curNew: number;
  favorite: boolean;
  activity: IActivity[];
  filterLevel: string;
  selectedOpts: string[];
  filterOpts: ICreateFilterOpts;
  template: string[];
  activeFilter: string;
  supQuestReady: boolean;
  questSentTo: string;
  colMapping: ICreateColMapping[];
  classificationValue: string;
  followUps: ICreateFollowUps[];
  aiStream: string[];
  eccnResults: any[];
  exNotes: string;
  chapterNotes: string;
  crossRulings: string[];
  aiSums: any[];
  tariffResults: any[];
  notOptions: string[];
  lastProcessedAt: Date;
  requestBody: IRequestContext;
  whitelisted: any[];
  removedHits: string[];
}

export interface IRequestContext {
  method: string;
  path: string;
  queryParams?: Record<string, any>;
  body?: Record<string, any>;
  ipAddress?: string;
}

export interface IFollowUps {
  question: string;
  answer: string;
}

export interface ICreateFollowUps {
  question: IFollowUps['question'];
  answer: IFollowUps['answer'];
}

export interface IColMapping {
  prop: string;
  val: string;
}

export interface ICreateColMapping {
  prop: IColMapping['prop'];
  val: IColMapping['val'];
}
export interface IFilterOpts {
  Agency: string[];
  Lens: string[];
  List: string[];
  Country: string[];
}

export interface ICreateFilterOpts {
  Agency: IFilterOpts['Agency'];
  Lens: IFilterOpts['Lens'];
  List: IFilterOpts['List'];
  Country: IFilterOpts['Country'];
}
export interface IActivity {
  user: IUser;
  action: string;
  timeStamp: Date;
}
export interface ISearchFilter {
  _id?: string;
  userId: string;
  groupId: string;
  filterName: string;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  updatedBy: string;
  type: string;
  lenses: string[];
  countries: string[];
  agencies: string[];
  lists: string[];
  entityCountries?: string[];
  entityTypes?: string[];
  hsCodeLists: string[];
  sections: string[];
  chapters: string[];
  codes: string[];
  entRes: boolean;
  filterDate: string[];
}
export interface IWorkflowEntry {
  name: string;
  assignees: string[];
  isComplete: boolean;
  completedAt: Date;
}

export interface IEntityInfo {
  primary: string;
  address: string;
  country: string;
}

export interface ICreateEntityInfo {
  primary?: IEntityInfo['primary'];
  address?: IEntityInfo['address'];
  country?: IEntityInfo['country'];
}

export interface ICommodityInfo {
  commodityName: string;
  harmonizedScheduleCode: string;
  countryOfOrigin: string;
  transhipmentCountry: string;
  destinationCountry: string;
  material: string;
  intendedUse: string;
  physicalState: string;
}

export interface ICreateCommodityInfo {
  commodityName: ICommodityInfo['commodityName'];
  harmonizedScheduleCode: ICommodityInfo['harmonizedScheduleCode'];
  countryOfOrigin: ICommodityInfo['countryOfOrigin'];
  transhipmentCountry: ICommodityInfo['transhipmentCountry'];
  destinationCountry: ICommodityInfo['destinationCountry'];
  material: ICommodityInfo['material'];
  intendedUse: ICommodityInfo['intendedUse'];
  physicalState: ICommodityInfo['physicalState'];
}
export interface IFile {
  name: string;
  base64: string;
  source: boolean;
  dateAdded: Date;
}
export interface ICaseDocument {
  case_id: string;
  uploadedFile: IFile;
}

export interface ICreateCaseDocument {
  _id: ICaseDocument['case_id'];
  case_id: ICaseDocument['case_id'];
  uploadedFile: ICaseDocument['uploadedFile'];
}

export interface IComments {
  content: string;
  person: string;
  date: string;
}

export interface ICreateComments {
  content: IComments['content'];
  person: IComments['person'];
  date: IComments['date'];
}

export interface IResults extends Document {
  allResults: string[];
  allTotal: number;
  newResults: string[];
  newTotal: number;
  date: string;
  docInfo: IDocItem[][];
}
export interface IDocItem {
  type: string;
  searchVal: string;
  row: number;
}

export interface ICreateResults {
  allResults: IResults['allResults'];
  allTotal: IResults['allTotal'];
  newResults: IResults['newResults'];
  newTotal: IResults['newTotal'];
  date: IResults['date'];
  docInfo: IResults['docInfo'];
}

export interface ICreateCase {
  _id?: ICase['_id'];
  date?: ICase['date'];
  ticketName?: ICase['ticketName'];
  autoId?: ICase['autoId'];
  entity?: ICase['entity'];
  commodity?: ICase['commodity'];
  documents?: ICase['documents'];
  comments?: ICase['comments'];
  owner?: ICase['owner'];
  createdBy?: ICase['createdBy'];
  createdAt?: ICase['createdAt'];
  assignee?: ICase['assignee'];
  groupId?: ICase['groupId'];
  groupName?: ICase['groupName'];
  userId?: ICase['userId'];
  docType?: ICase['docType'];
  results?: ICase['results'];
  searchedProps?: ICase['searchedProps'];
  workflowEntry?: ICase['workflowEntry'];
  workflowData?: ICase['workflowData'];
  sharedWith?: ICase['sharedWith'];
  status?: ICase['status'];
  type?: ICase['type'];
  entityFilter?: ICase['entityFilter'];
  commodityFilter?: ICase['commodityFilter'];
  frequency?: ICase['frequency'];
  lastUpdate?: ICase['lastUpdate'];
  curTotal?: ICase['curTotal'];
  curNew?: ICase['curNew'];
  favorite?: ICase['favorite'];
  activity?: ICase['activity'];
  filterLevel?: ICase['filterLevel'];
  selectedOpts?: ICase['selectedOpts'];
  filterOpts?: ICase['filterOpts'];
  template?: ICase['template'];
  activeFilter?: ICase['activeFilter'];
  supQuestReady?: ICase['supQuestReady'];
  questSentTo?: ICase['questSentTo'];
  colMapping?: ICase['colMapping'];
  classificationValue?: ICase['classificationValue'];
  followUps?: ICase['followUps'];
  aiStream?: ICase['aiStream'];
  eccnResults?: ICase['eccnResults'];
  exNotes?: ICase['exNotes'];
  chapterNotes?: ICase['chapterNotes'];
  crossRulings?: ICase['crossRulings'];
  aiSums?: ICase['aiSums'];
  tariffResults?: ICase['tariffResults'];
  notOptions?: ICase['notOptions'];
  lastProcessedAt?: ICase['lastProcessedAt'];
  requestBody?: ICase['requestBody'];
  whitelisted?: ICase['whitelisted'];
  removedHits?: ICase['removedHits'];
}

interface IGlobalState {
  tenant_access: ITenantAccess;
  cur_user: IUser;
  userOpts: any[];
  isWhiteLabel: boolean;
  sessionInvalidated: boolean;
  handleTeamChange: any;
  initKeycloakUser: any;
  refreshCurUser: () => Promise<void>;
  // new ticket state
  ticketDraft: Partial<ICreateCase>;
  setTicketDraft: (data: Partial<ICreateCase>) => void;
  resetTicketDraft: () => void;
  // True only during the initial-mount window right after a forced re-login
  // redirect restored a captured ticketDraft; false otherwise (including for
  // the rest of the session, once that window passes). The three workflow
  // pages -- AND the deeper components they render (ChatUI, EARChatUI,
  // BeneficialOwnershipWorkflow), which independently reset ticketDraft on
  // their own mount -- all check this before resetting on arrival, so the
  // restore isn't immediately overwritten. Deliberately a plain read, not a
  // one-shot "consume": multiple components need to see `true` during the
  // same initial mount (child effects run before parent effects in React, so
  // whichever reads first can't be the one that clears it for everyone
  // else). GlobalProvider clears it back to false shortly after mount via
  // setTimeout(0), once every initial-mount effect has had its turn.
  wasTicketDraftRestored: () => boolean;
  theme: string;
  setTheme: (t: string) => void;
  toggleTheme: () => void;
  refreshSelectedTeamLicenseState: () => void;
}

interface IUserPreferencePayload {
  theme_preference?: string;
  team?: string;
  preferred_language?: string;
}

const empty_usr: IUser = {
  basics: undefined,
  teams: [],
  isAdmin: false,
  selected_team: { id: 'EMPTY_TEAM', name: 'EMPTY_TEAM', path: 'EMPTY_TEAM' },
  bite_super_admin: false,
  service_account: false,
  modules: {},
  stripe_cus_info: [],
  license: 'none',
  role: 'contributor',
  awsUser: false,
  quickcodeOnly: false,
  hasEntityAddon: false,
};

const initialState: IGlobalState = {
  tenant_access: {},
  cur_user: empty_usr,
  userOpts: [],
  isWhiteLabel: false,
  sessionInvalidated: false,

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleTeamChange: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  initKeycloakUser: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  refreshCurUser: async () => {},

  ticketDraft: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setTicketDraft: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  resetTicketDraft: () => {},
  wasTicketDraftRestored: () => false,
  theme: 'dark',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setTheme: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  toggleTheme: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  refreshSelectedTeamLicenseState: () => {},
};

export const GlobalContext = createContext(initialState);

interface IGlobalProviderProps {
  children?: React.JSX.Element | React.JSX.Element[];
}

export const GlobalProvider: React.FC<IGlobalProviderProps> = ({
  children,
}) => {
  const startedRef = useRef(false);

  // Runs synchronously during GlobalProvider's own first render, before any
  // child (including the three workflow pages' own reset-on-arrival effects)
  // ever renders -- so wasTicketDraftRestored is already correct by the time
  // anything downstream reads it. Deliberately not done in a useEffect: effect
  // ordering between this provider and a child page's mount effect isn't
  // something to depend on for correctness.
  const wasTicketDraftRestoredRef = useRef(false);
  const [ticketDraft, setTicketDraftState] = useState<Partial<ICreateCase>>(
    () => {
      const draft = loadDraft<Partial<ICreateCase>>(TICKET_DRAFT_RECOVERY_KEY);
      if (draft && Object.keys(draft).length > 0) {
        wasTicketDraftRestoredRef.current = true;
        return draft;
      }
      return {};
    },
  );

  const setTicketDraft = (data: Partial<ICreateCase>) => {
    setTicketDraftState((prev) => ({ ...prev, ...data }));
  };

  const resetTicketDraft = () => {
    setTicketDraftState({});
  };

  const wasTicketDraftRestored = () => wasTicketDraftRestoredRef.current;

  // Only meaningful when wasTicketDraftRestoredRef.current started true (i.e.
  // a draft actually was restored). setTimeout(0) is a macrotask -- it runs
  // strictly after every synchronous passive-effect flush from the initial
  // commit (both this provider's own effects and every descendant's,
  // regardless of child-vs-parent ordering), so every mount-time consumer
  // downstream has had its chance to read `true` before this flips it back.
  useEffect(() => {
    if (!wasTicketDraftRestoredRef.current) return undefined;
    const timer = setTimeout(() => {
      wasTicketDraftRestoredRef.current = false;
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  // Keeps the recoverable registry pointed at the latest ticketDraft so a
  // forced re-login redirect (see query-client.ts's handleAuthError) can
  // snapshot whatever's actually in it at that moment, not a stale closure.
  const ticketDraftRef = useRef(ticketDraft);
  ticketDraftRef.current = ticketDraft;

  useEffect(() => {
    return registerRecoverable(
      TICKET_DRAFT_RECOVERY_KEY,
      () => ticketDraftRef.current,
    );
  }, []);

  const queryParameters = new URLSearchParams(window.location.search);

  const awsUserAccessToken = queryParameters.get('accessToken');
  const awsUserRefreshToken = queryParameters.get('refreshToken');
  if (awsUserAccessToken && awsUserRefreshToken) {
    localStorage.setItem('awsUserAccessToken', awsUserAccessToken);
    localStorage.setItem('awsUserRefreshToken', awsUserRefreshToken);
  }
  const awsUserTokenPayload = {
    accessToken: localStorage.getItem('awsUserAccessToken'),
    refreshToken: localStorage.getItem('awsUserRefreshToken'),
  };
  const { enqueueSnackbar } = useSnackbar();
  const [tenantAccess, setTenantAccess] = useState<ITenantAccess>({
    tenantAccess: [],
    config: undefined,
    create_at: undefined,
    created_by: undefined,
    domain: undefined,
    group_id: undefined,
    id: undefined,
    is_active: false,
    name: undefined,
    updated_at: undefined,
    updated_by: undefined,
  });
  const [isWhiteLabel, setIsWhiteLabel] = useState<boolean>(false);
  const [sessionInvalidated, setSessionInvalidated] = useState<boolean>(false);
  const [userOpts, setUserOpts] = useState<{
    isLoading: boolean;
    error: string | undefined;
    data: any[];
  }>({ isLoading: true, error: undefined, data: [] });
  const [curUser, setCurUser] = useState<{
    isLoading: boolean;
    error: string | undefined;
    data: IUser;
  }>({ isLoading: true, error: undefined, data: empty_usr });

  const [theme, setTheme] = useState('dark');

  const checkEntityAddonRole = async (teamId: string): Promise<boolean> => {
    if (!teamId || teamId === 'EMPTY_TEAM') return false;

    try {
      const res = await GroupAPI.getGroupRealmRoles(teamId);
      if (
        res?.success &&
        Array.isArray(res.result) &&
        res.result.some((role: any) => role.name === 'entity-addon')
      ) {
        return true;
      }
      // A team given Business Verification credits by hand has no Stripe
      // package to switch the role on, but is still entitled to use them.
      const credits = await CreditsAPI.getVerificationCreditsSummary(teamId);
      return Boolean(
        credits?.success && Number(credits.result?.verification_credits) > 0,
      );
    } catch (error) {
      console.error('Failed to check entity addon role:', error);
    }
    return false;
  };

  const parseBooleanAttribute = (value: unknown): boolean => {
    if (Array.isArray(value)) {
      return value.some((item) => parseBooleanAttribute(item));
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1';
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    return value === true;
  };

  const hasQuickcodeAttribute = (
    attributes?: Record<string, unknown>,
  ): boolean => {
    if (!attributes) {
      return false;
    }
    const quickcodeValue = attributes.quickcode;
    return parseBooleanAttribute(quickcodeValue);
  };

  const hasQuickcodeForTeam = (stripeCustomers: any[], teamName?: string) => {
    if (!Array.isArray(stripeCustomers) || !teamName) {
      return false;
    }
    const normalizedTeamName = teamName.trim().toLowerCase();
    return stripeCustomers.some((rawCustomer: any) => {
      const customer = rawCustomer?.data ?? rawCustomer;
      const customerName =
        typeof customer?.name === 'string'
          ? customer.name.trim().toLowerCase()
          : '';
      if (customerName !== normalizedTeamName) {
        return false;
      }
      const subscriptions = Array.isArray(customer?.subscriptions?.data)
        ? customer.subscriptions.data
        : [];
      return subscriptions.some((subscription: any) => {
        const moduleName =
          typeof subscription?.metadata?.moduleName === 'string'
            ? subscription.metadata.moduleName.trim().toLowerCase()
            : '';
        return moduleName === 'quickcode';
      });
    });
  };

  const getUsersForTeam = async (teamId: string) => {
    if (!teamId || teamId === 'EMPTY_TEAM') {
      setUserOpts({ isLoading: false, error: undefined, data: [] });
      return;
    }

    UsersAPI.getUsers(teamId)?.then((res) => {
      if (res?.success) {
        const user_opts = res.result?.map((u: any) => ({
          label: u.email,
          value: u.id,
        }));
        setUserOpts({ isLoading: false, error: undefined, data: user_opts });
      } else {
        const err_text = `Error loading Training Users: ${res?.result}`;
        enqueueSnackbar(err_text, { variant: 'error' });
        setUserOpts({ isLoading: false, error: err_text, data: [] });
      }
    });
  };

  const getLicenseAndRole = async (teamId: string, userId: string) => {
    const defaultLicenseAndRole = { license: 'none', role: 'contributor' };
    const licenseAndRole = await LicensesAPI.getMyLicense(teamId, userId);

    let finalLicense = defaultLicenseAndRole.license;
    let finalRole = defaultLicenseAndRole.role;

    // Check if the API call was successful to map the fetched values
    if (licenseAndRole?.success && licenseAndRole?.result) {
      finalLicense =
        licenseAndRole.result.license || defaultLicenseAndRole.license;
      finalRole = licenseAndRole.result.role || defaultLicenseAndRole.role;
    }

    // Hardcoded license override for the specific team
    if (teamId === '7b5f8102-2684-457c-95d9-3e9963985599') {
      finalLicense = 'tariff-only';
    }

    return {
      license: finalLicense,
      role: finalRole,
    };
  };
  const applyUserPreferences = async (userId: string, thisUser: IUser) => {
    const preferenceRes = await UsersAPI.getUserPreferencesByUserId(userId);
    if (!preferenceRes?.success || !preferenceRes?.result?.success) {
      return;
    }

    const preference = preferenceRes.result?.data as
      | IUserPreferencePayload
      | undefined;
    if (!preference) {
      return;
    }

    if (
      preference.theme_preference === 'light' ||
      preference.theme_preference === 'dark'
    ) {
      setThemePreference(preference.theme_preference);
    }

    if (preference.team && thisUser.teams.length > 0) {
      const preferredTeam = thisUser.teams.find(
        (team) =>
          team?.name?.trim().toLowerCase() ===
          preference.team?.trim().toLowerCase(),
      );
      if (preferredTeam) {
        thisUser.selected_team = preferredTeam;
      }
    }
  };

  const saveUserPreferences = async (
    userId: string,
    preference: IUserPreferencePayload,
  ) => {
    if (!userId) {
      return;
    }

    try {
      const existingPreference = await UsersAPI.getUserPreferencesByUserId(
        userId,
      );
      const hasExistingPreference =
        existingPreference?.success &&
        existingPreference?.result?.success &&
        Boolean(existingPreference?.result?.data);

      if (hasExistingPreference) {
        const updateRes = await UsersAPI.updateUserPreferencesByUserId(
          userId,
          preference,
        );
        if (!updateRes?.success) {
          console.error(
            'Failed to update user preferences:',
            updateRes?.result,
          );
        }
        return;
      }

      const createRes = await UsersAPI.createUserPreferences({
        user_id: userId,
        theme_preference: preference.theme_preference,
        team: preference.team,
        preferred_language: preference.preferred_language,
      });
      if (!createRes?.success) {
        console.error('Failed to create user preferences:', createRes?.result);
      }
    } catch (error) {
      console.error('Failed saving user preferences:', error);
    }
  };

  const setThemePreference = async (nextTheme: string) => {
    setTheme(nextTheme);
  };

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setThemePreference(nextTheme);
  };

  const initKeycloakUser = async () => {
    try {
      const domains = BITE_DOMAINS ? BITE_DOMAINS.split(/,\s*/) : [''];
      //const domains = [''];
      const currentHost = window.location.host;
      const isNotBiteDomain = domains.indexOf(currentHost) === -1;
      setIsWhiteLabel(isNotBiteDomain);

      const buildFallbackUser = (user: any) =>
        ({
          basics: {
            id: user.id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            username: user.username,
            attributes: {},
          },
          teams: [],
          isAdmin: false,
          selected_team: {
            id: 'EMPTY_TEAM',
            name: 'EMPTY_TEAM',
            path: 'EMPTY_TEAM',
          },
          bite_super_admin: false,
          service_account: false,
          modules: {},
          stripe_cus_info: [],
          license: 'none',
          role: 'contributor',
          awsUser: false,
          quickcodeOnly: false,
          hasEntityAddon: false,
        } as IUser);

      const successCallback = async (user: any) => {
        let resolvedTenantAccess: ITenantAccess = {
          tenantAccess: [],
          is_active: false,
          domain: undefined,
        };

        let resolvedIsWhiteLabel = isNotBiteDomain;
        let targetDomain = currentHost;

        if (user.is_jwt_login) {
          resolvedIsWhiteLabel = true;
          targetDomain = user.jwt_domain;
        }

        // Commit isWhiteLabel as soon as it's resolved, not gated behind the
        // getUser/tenant fetch below — a 401 there (e.g. this tab's session
        // was invalidated by a newer tab) must not leave isWhiteLabel stuck
        // at its false default, or Routes.tsx will never route to the
        // white-label / NoTenantAccess flow at all.
        setIsWhiteLabel(resolvedIsWhiteLabel);

        // 1. FIRST: Run the domain sync and FORCE the token refresh
        // This ensures the Keycloak token has the correct roles before hitting the Tenant API
        try {
          const syncRes = await UsersAPI.syncDomainGroup();
          const isUpdated =
            syncRes?.result?.result?.updated || syncRes?.result?.updated;
          if (syncRes?.success && isUpdated) {
            sessionStorage.removeItem('curr_team');
            await KeycloakService.updateToken(-1); // Refreshes token with new permissions
          }
        } catch (syncError) {
          console.error('Failed to sync domain group:', syncError);
        }

        // 1b. Apply SCIM-provisioned entitlements, before the profile is read.
        // A SCIM user is written directly into Keycloak by the customer's IdP, so
        // they have no team membership and no license record yet. getUser below
        // would return teams: [], which skips the entire license block, so this
        // has to land first — and the token needs refreshing afterwards for the
        // new group membership to appear in its claims.
        try {
          const scimRes = await ScimAPI.reconcileMe();
          if (scimRes?.success && scimRes?.result?.data?.applied) {
            sessionStorage.removeItem('curr_team');
            await KeycloakService.updateToken(-1);
          } else if (!scimRes?.success && scimRes?.status === 401) {
            // A newer tab minted its own JWT session for this user, which
            // invalidates the session this tab was relying on. Keep this
            // tab's isWhiteLabel/tenant_access as resolved below so the app
            // doesn't flash a broken state, but flag it so Routes.tsx routes
            // to NoTenantAccess instead of the normal authenticated app.
            console.error(
              'Tab session is no longer valid. New tab has been opened.',
            );
            setSessionInvalidated(true);
          }
        } catch (scimError) {
          // Never block a sign-in on reconciliation. The user lands with whatever
          // entitlements they already had; the next login retries.
          console.error('Failed to reconcile SCIM entitlements:', scimError);
        }

        // 2. Fetch the User Profile
        const resp = await UsersAPI.getUser(user.id);

        if (resp?.success) {
          const thisUser = resp.result;
          thisUser.service_account = await KeycloakService.isServiceAccount();
          if (thisUser.service_account) {
            KeycloakService.doLogout();
            return;
          }

          const teamIds = thisUser.teams.map((t: any) => t.id).filter(Boolean);
          const [rolesMap, membersMap, attrsMap] = await Promise.all([
            GroupAPI.getGroupRolesBatch(teamIds).then(
              (r: any) => r.result ?? {},
            ),
            UsersAPI.getUsersBatch(teamIds).then((r: any) => r.result ?? {}),
            GroupAPI.getGroupsBatch(teamIds).then((r: any) => r.result ?? {}),
          ]);

          thisUser.teams = thisUser.teams.filter((team: any) => {
            const roles: any[] = rolesMap[team.id] ?? [];
            if (roles.some((r: any) => r.name === 'archived')) return false;
            team.members = membersMap[team.id] ?? [];
            team.attributes = attrsMap[team.id]?.attributes ?? team.attributes;
            return true;
          });

          const lastAccess = Date.now();
          const quickcodeFromAttribute = hasQuickcodeAttribute(
            thisUser?.basics?.attributes,
          );
          thisUser.quickcodeOnly = quickcodeFromAttribute;
          thisUser.bite_super_admin = await KeycloakService.isBiteAdmin();

          const stripeRes = await PaymentsAPI.getCustomerByEmail(
            thisUser.basics.email,
          );
          if (stripeRes.success) {
            thisUser.stripe_cus_info = stripeRes.result.data;
            if (thisUser.teams.length > 0) {
              const universalTeamId =
                process.env.REACT_APP_UNIVERSAL_ACCESS_GROUP_ID; //eslint-disable-line
              const universalTeam = thisUser.teams.find(
                (t: any) => t.id === universalTeamId,
              );

              const currTeamCache = sessionStorage.getItem('curr_team');
              let selected_team = null;

              if (currTeamCache && currTeamCache !== '-1') {
                selected_team = thisUser.teams[JSON.parse(currTeamCache)];
              }
              if (!selected_team) {
                selected_team = universalTeam || thisUser.teams[0];
              }

              thisUser.selected_team = selected_team;
              thisUser.hasEntityAddon = await checkEntityAddonRole(
                thisUser.selected_team.id,
              );
              await applyUserPreferences(thisUser.basics.id, thisUser);

              thisUser.selected_team.attributes =
                attrsMap[thisUser.selected_team.id]?.attributes ??
                thisUser.selected_team.attributes;
              thisUser.quickcodeOnly =
                quickcodeFromAttribute ||
                hasQuickcodeForTeam(
                  thisUser.stripe_cus_info,
                  thisUser.selected_team?.name,
                );

              await LicensesAPI.getLicensesForTeam(thisUser.selected_team.id);

              const licenseAndRole = await getLicenseAndRole(
                thisUser.selected_team.id,
                thisUser.basics.id,
              );
              thisUser.license = licenseAndRole.license;
              thisUser.role = licenseAndRole.role;
            }

            const userTeamNames = thisUser?.teams?.map((t: any) => t.name);
            await DatahubAPI.writeToIndex({
              user_id: thisUser?.basics?.id,
              user_email: thisUser?.basics?.email,
              firstName: thisUser?.basics?.firstName
                ? thisUser?.basics?.firstName
                : 'N/A',
              lastName: thisUser?.basics?.lastName
                ? thisUser?.basics?.lastName
                : 'N/A',
              teams: thisUser?.teams?.length > 0 ? userTeamNames : [],
              lastAccess: lastAccess,
              firstLogin: thisUser?.basics?.attributes?.terms_and_conditions
                ? thisUser?.basics?.attributes?.terms_and_conditions[0]
                : '',
              domain: currentHost,
            });
          } else {
            thisUser.stripe_cus_info = [];
          }

          // 3. NOW fetch Tenant context using the fully refreshed token
          if (resolvedIsWhiteLabel) {
            try {
              // Fetch Host Config
              const hostRes = await TenantAPI.getTenantAccessHost(
                targetDomain,
                thisUser?.basics?.id,
              );
              if (hostRes?.success && hostRes?.result?.data) {
                resolvedTenantAccess = {
                  ...resolvedTenantAccess,
                  ...hostRes.result.data,
                };
              }

              // Fetch User Permissions
              const accessRes = await TenantAPI.getTenantAccess(
                targetDomain,
                thisUser?.basics?.id,
              );
              if (accessRes?.success && accessRes?.result?.data) {
                resolvedTenantAccess = {
                  ...resolvedTenantAccess,
                  ...accessRes.result.data,
                  domain:
                    resolvedTenantAccess.domain || accessRes.result.data.domain,
                };
              }
            } catch (e: any) {
              console.error('Tenant fetch failed:', e);
            }
          }

          // 4. Hard Fallback Protections
          if (!resolvedTenantAccess.domain) {
            resolvedTenantAccess.domain = targetDomain;
          }
          if (!resolvedTenantAccess.tenantAccess) {
            resolvedTenantAccess.tenantAccess = [];
          }

          // 5. Batch commit everything safely to state
          // (isWhiteLabel was already committed above, right after it resolved)
          setTenantAccess(resolvedTenantAccess);
          setCurUser({ isLoading: false, error: undefined, data: thisUser });

          getUsersForTeam(thisUser.selected_team?.id);
          sessionStorage.setItem(
            'curr_team',
            JSON.stringify(thisUser.teams.indexOf(thisUser.selected_team)),
          );
        } else if (
          // eslint-disable-next-line no-undef
          process.env.NODE_ENV === 'development' &&
          typeof resp?.result === 'string' &&
          resp.result.includes('404')
        ) {
          const fallbackUser = buildFallbackUser(user);
          setCurUser({
            isLoading: false,
            error: undefined,
            data: fallbackUser,
          });
          sessionStorage.removeItem('curr_team');
        } else {
          const error_msg = `Failed pulling info for your user - ${resp?.result}`;
          enqueueSnackbar(error_msg, { variant: 'error' });
          setCurUser({ isLoading: false, error: error_msg, data: empty_usr });
        }
      };
      const awsSuccessCallback = async (user: any) => {
        const resp = await UsersAPI.getUser(user.id);
        if (resp?.success) {
          const thisUser = resp.result;
          thisUser.service_account = await KeycloakService.isServiceAccount();
          if (thisUser.service_account) {
            KeycloakService.doLogout();
            // eslint-disable-next-line no-alert
            alert('Service accounts do not have application level access');
            return;
          }
          // Batch-fetch roles, members, and attributes for all teams in 3 requests.
          const awsTeamIds = thisUser.teams
            .map((t: any) => t.id)
            .filter(Boolean);
          const [awsRolesMap, awsMembersMap, awsAttrsMap] = await Promise.all([
            GroupAPI.getGroupRolesBatch(awsTeamIds).then(
              (r: any) => r.result ?? {},
            ),
            UsersAPI.getUsersBatch(awsTeamIds).then((r: any) => r.result ?? {}),
            GroupAPI.getGroupsBatch(awsTeamIds).then(
              (r: any) => r.result ?? {},
            ),
          ]);
          thisUser.teams = thisUser.teams.filter((team: any) => {
            const roles: any[] = awsRolesMap[team.id] ?? [];
            if (roles.some((r: any) => r.name === 'archived')) return false;
            team.members = awsMembersMap[team.id] ?? [];
            team.attributes =
              awsAttrsMap[team.id]?.attributes ?? team.attributes;
            return true;
          });
          thisUser.awsUser = true;
          thisUser.bite_super_admin = await KeycloakService.isBiteAdmin();
          const quickcodeFromAttribute = hasQuickcodeAttribute(
            thisUser?.basics?.attributes,
          );
          thisUser.quickcodeOnly = quickcodeFromAttribute;
          thisUser.stripe_cus_info = thisUser.stripe_cus_info || [];
          if (thisUser.teams.length > 0) {
            const universalTeamId =
              process.env.REACT_APP_UNIVERSAL_ACCESS_GROUP_ID; //eslint-disable-line
            const universalTeam = thisUser.teams.find(
              (t: any) => t.id === universalTeamId,
            );

            const currTeamCache = sessionStorage.getItem('curr_team');
            let selected_team = null;

            if (currTeamCache && currTeamCache !== '-1') {
              selected_team = thisUser.teams[JSON.parse(currTeamCache)];
            }

            if (!selected_team) {
              selected_team = universalTeam || thisUser.teams[0];
            }

            thisUser.selected_team = selected_team;
            // attributes already fetched in batch above
            thisUser.selected_team.attributes =
              awsAttrsMap[thisUser.selected_team.id]?.attributes ??
              thisUser.selected_team.attributes;
            thisUser.quickcodeOnly =
              quickcodeFromAttribute ||
              hasQuickcodeForTeam(
                thisUser.stripe_cus_info,
                thisUser.selected_team?.name,
              );
            const licenseAndRole = await getLicenseAndRole(
              thisUser.selected_team.id,
              thisUser.basics.id,
            );
            thisUser.license = licenseAndRole.license;
            thisUser.role = licenseAndRole.role;
            await applyUserPreferences(thisUser.basics.id, thisUser);
          }
          setCurUser({ isLoading: false, error: undefined, data: thisUser });
          getUsersForTeam(thisUser.selected_team?.id);
          sessionStorage.setItem(
            'curr_team',
            JSON.stringify(thisUser.teams.indexOf(thisUser.selected_team)),
          );
        } else if (
          // eslint-disable-next-line no-undef
          process.env.NODE_ENV === 'development' &&
          typeof resp?.result === 'string' &&
          resp.result.includes('404')
        ) {
          const fallbackUser = buildFallbackUser(user);
          setCurUser({
            isLoading: false,
            error: undefined,
            data: fallbackUser,
          });
          sessionStorage.removeItem('curr_team');
        } else {
          const error_msg = `Failed pulling info for your user - ${resp?.result}`;
          enqueueSnackbar(error_msg, { variant: 'error' });
          setCurUser({ isLoading: false, error: error_msg, data: empty_usr });
        }
      };
      if (awsUserTokenPayload.accessToken && awsUserTokenPayload.refreshToken) {
        await KeycloakService.initKeycloak(
          awsSuccessCallback,
          awsUserTokenPayload,
        );
      } else {
        await KeycloakService.initKeycloak(successCallback, null);
      }
    } catch (e: any) {
      const error_msg = `Failed pulling info for your user - ${
        e?.message || 'Unknown error'
      }`;
      enqueueSnackbar(error_msg, { variant: 'error' });
      setCurUser({ isLoading: false, error: error_msg, data: empty_usr });
    }
  };

  const handleTeamChange = async (newTeam: ITeam) => {
    // const modules = await GroupAPI.getGroupRealmRoles(newTeam.id);
    const attributes = await GroupAPI.getGroupById(newTeam.id);
    newTeam.attributes = attributes?.result?.attributes;
    await LicensesAPI.getLicensesForTeam(newTeam.id);
    const quickcodeFromAttribute = hasQuickcodeAttribute(
      curUser.data?.basics?.attributes,
    );
    const licenseAndRole = await getLicenseAndRole(
      newTeam.id,
      curUser.data?.basics?.id || '',
    );
    const hasEntityAddon = await checkEntityAddonRole(newTeam.id);
    const newUser = {
      ...curUser.data,
      selected_team: newTeam,
      license: licenseAndRole.license,
      role: licenseAndRole.role,
      hasEntityAddon: hasEntityAddon,
      quickcodeOnly:
        quickcodeFromAttribute ||
        hasQuickcodeForTeam(curUser.data.stripe_cus_info, newTeam?.name),
    };
    setCurUser({ isLoading: false, error: undefined, data: newUser });
    getUsersForTeam(newTeam.id);
    sessionStorage.setItem(
      'curr_team',
      JSON.stringify(newUser.teams.indexOf(newUser.selected_team)),
    );
  };

  const refreshSelectedTeamLicenseState = async () => {
    const teamId = curUser.data?.selected_team?.id;
    const userId = curUser.data?.basics?.id;

    if (!teamId || !userId || teamId === 'EMPTY_TEAM') {
      return;
    }

    try {
      // Reconcile team license assignments on the backend
      await LicensesAPI.getLicensesForTeam(teamId);

      // Re-read the current user's team-scoped license + role
      const licenseAndRole = await getLicenseAndRole(teamId, userId);

      const quickcodeFromAttribute = hasQuickcodeAttribute(
        curUser.data?.basics?.attributes,
      );
      const hasEntityAddon = await checkEntityAddonRole(teamId);

      // Stripe customers were only ever read at login, so anything created since
      // — a new team, or one set up by hand — stayed invisible to every consumer
      // of stripe_cus_info until the next sign-in. Refresh it here so switching
      // teams picks up current billing state.
      let refreshedStripeInfo = curUser.data?.stripe_cus_info;
      try {
        const stripeRes = await PaymentsAPI.getCustomerByEmail(
          curUser.data?.basics?.email,
        );
        if (stripeRes?.success && Array.isArray(stripeRes.result?.data)) {
          refreshedStripeInfo = stripeRes.result.data;
        }
      } catch (stripeError) {
        // Keep whatever we already had; billing views degrade rather than break.
        console.error('Failed to refresh Stripe customers:', stripeError);
      }

      setCurUser((prev) => ({
        isLoading: false,
        error: undefined,
        data: {
          ...prev.data,
          license: licenseAndRole.license,
          role: licenseAndRole.role,
          stripe_cus_info: refreshedStripeInfo ?? prev.data.stripe_cus_info,
          quickcodeOnly:
            quickcodeFromAttribute ||
            hasQuickcodeForTeam(
              refreshedStripeInfo ?? prev.data.stripe_cus_info,
              prev.data.selected_team?.name,
            ),
          hasEntityAddon: hasEntityAddon,
        },
      }));

      await getUsersForTeam(teamId);
    } catch (error) {
      console.error('Failed to refresh selected team license state:', error);
      enqueueSnackbar('Unable to refresh team license state', {
        variant: 'error',
      });
    }
  };

  /**
   * Re-fetches teams/license/role/stripe info for the already-authenticated
   * user, without touching Keycloak's own session -- `_kc.init()` (inside
   * KeycloakService.initKeycloak, which initKeycloakUser calls) can only run
   * once per page load and throws if called again. This is for the one case
   * neither handleTeamChange nor refreshSelectedTeamLicenseState covers: a
   * brand-new team created THIS session (e.g. from LoginWizard) that isn't in
   * `cur_user.teams` yet at all, so route guards like requiresTeamOnboarding
   * still see a team-less user until this runs.
   */
  const refreshCurUser = async () => {
    try {
      const authUser = KeycloakService.getUser();
      if (!authUser?.id) return;

      const resp = await UsersAPI.getUser(authUser.id);
      if (!resp?.success) return;

      const thisUser = resp.result;
      thisUser.service_account = await KeycloakService.isServiceAccount();

      const teamIds = thisUser.teams.map((t: any) => t.id).filter(Boolean);
      const [rolesMap, membersMap, attrsMap] = await Promise.all([
        GroupAPI.getGroupRolesBatch(teamIds).then((r: any) => r.result ?? {}),
        UsersAPI.getUsersBatch(teamIds).then((r: any) => r.result ?? {}),
        GroupAPI.getGroupsBatch(teamIds).then((r: any) => r.result ?? {}),
      ]);

      thisUser.teams = thisUser.teams.filter((team: any) => {
        const roles: any[] = rolesMap[team.id] ?? [];
        if (roles.some((r: any) => r.name === 'archived')) return false;
        team.members = membersMap[team.id] ?? [];
        team.attributes = attrsMap[team.id]?.attributes ?? team.attributes;
        return true;
      });

      const quickcodeFromAttribute = hasQuickcodeAttribute(
        thisUser?.basics?.attributes,
      );
      thisUser.quickcodeOnly = quickcodeFromAttribute;
      thisUser.bite_super_admin = await KeycloakService.isBiteAdmin();

      const stripeRes = await PaymentsAPI.getCustomerByEmail(
        thisUser.basics.email,
      );
      thisUser.stripe_cus_info = stripeRes.success ? stripeRes.result.data : [];

      if (thisUser.teams.length > 0) {
        const universalTeamId = process.env.REACT_APP_UNIVERSAL_ACCESS_GROUP_ID; //eslint-disable-line
        const universalTeam = thisUser.teams.find(
          (t: any) => t.id === universalTeamId,
        );
        const currTeamCache = sessionStorage.getItem('curr_team');
        let selected_team = null;
        if (currTeamCache && currTeamCache !== '-1') {
          selected_team = thisUser.teams[JSON.parse(currTeamCache)];
        }
        if (!selected_team) {
          selected_team = universalTeam || thisUser.teams[0];
        }

        thisUser.selected_team = selected_team;
        thisUser.hasEntityAddon = await checkEntityAddonRole(
          thisUser.selected_team.id,
        );
        await applyUserPreferences(thisUser.basics.id, thisUser);

        thisUser.selected_team.attributes =
          attrsMap[thisUser.selected_team.id]?.attributes ??
          thisUser.selected_team.attributes;
        thisUser.quickcodeOnly =
          quickcodeFromAttribute ||
          hasQuickcodeForTeam(
            thisUser.stripe_cus_info,
            thisUser.selected_team?.name,
          );

        await LicensesAPI.getLicensesForTeam(thisUser.selected_team.id);
        const licenseAndRole = await getLicenseAndRole(
          thisUser.selected_team.id,
          thisUser.basics.id,
        );
        thisUser.license = licenseAndRole.license;
        thisUser.role = licenseAndRole.role;
      }

      setCurUser({ isLoading: false, error: undefined, data: thisUser });
      getUsersForTeam(thisUser.selected_team?.id);
      sessionStorage.setItem(
        'curr_team',
        JSON.stringify(thisUser.teams.indexOf(thisUser.selected_team)),
      );
    } catch (error) {
      console.error('Failed to refresh current user:', error);
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    initKeycloakUser();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const userId = curUser.data?.basics?.id;
    if (!userId) {
      return;
    }

    saveUserPreferences(userId, {
      theme_preference: theme,
      team: curUser.data?.selected_team?.name,
    }).catch((error) => {
      console.error('Preference autosave failed:', error);
    });
  }, [theme, curUser.data?.basics?.id, curUser.data?.selected_team?.name]);

  const state_val: IGlobalState = {
    handleTeamChange: handleTeamChange,
    initKeycloakUser: initKeycloakUser,
    refreshCurUser: refreshCurUser,
    userOpts: userOpts.data,
    cur_user: curUser.data,
    isWhiteLabel: isWhiteLabel,
    sessionInvalidated: sessionInvalidated,
    tenant_access: tenantAccess,
    ticketDraft: ticketDraft,
    setTicketDraft: setTicketDraft,
    resetTicketDraft: resetTicketDraft,
    wasTicketDraftRestored: wasTicketDraftRestored,
    theme: theme,
    setTheme: setThemePreference,
    toggleTheme: toggleTheme,
    refreshSelectedTeamLicenseState: refreshSelectedTeamLicenseState,
  };

  if (curUser.isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <GlobalContext.Provider value={state_val}>
      {children}
    </GlobalContext.Provider>
  );
};
